import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  AlertTriangle, 
  Clock, 
  FileWarning, 
  Car, 
  User, 
  ChevronRight,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Zap,
  Shield
} from 'lucide-react';

const RiskFlagsPanel = ({ compact = false }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchRiskFlags = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/platform-owner/risk-flags');
      setData(res.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching risk flags:', err);
      setError('Failed to load risk flags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRiskFlags();
    // Refresh every 2 minutes for urgent items
    const interval = setInterval(fetchRiskFlags, 120000);
    return () => clearInterval(interval);
  }, []);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'warning': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getTypeIcon = (type) => {
    if (type.includes('booking') || type.includes('prehire') || type.includes('posthire')) {
      return <FileWarning className="w-4 h-4" />;
    }
    if (type.includes('licence') || type.includes('license')) {
      return <User className="w-4 h-4" />;
    }
    if (type.includes('insurance')) {
      return <FileWarning className="w-4 h-4" />;
    }
    if (type.includes('rego') || type.includes('vehicle')) {
      return <Car className="w-4 h-4" />;
    }
    return <AlertTriangle className="w-4 h-4" />;
  };

  const handleActionClick = (actionUrl) => {
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  const getEscalationBadge = (tier) => {
    switch (tier) {
      case 3: return <Badge className="bg-red-600 text-white text-xs">CRITICAL</Badge>;
      case 2: return <Badge className="bg-orange-500 text-white text-xs">URGENT</Badge>;
      case 1: return <Badge className="bg-blue-500 text-white text-xs">NEW</Badge>;
      default: return null;
    }
  };

  if (loading && !data) {
    return (
      <Card className="border-slate-200" data-testid="risk-flags-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Risk & Action Flags
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50" data-testid="risk-flags-panel">
        <CardContent className="py-6">
          <p className="text-red-600 text-sm">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchRiskFlags} className="mt-2">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { summary, urgent_queue, critical, upcoming, action_required, data_notes } = data || {};
  const totalFlags = summary?.total_flags || 0;
  const hasUrgent = (summary?.urgent_queue_count || 0) > 0;
  const hasCritical = (summary?.critical_count || 0) > 0;
  const hasUpcoming = (summary?.upcoming_count || 0) > 0;
  const hasActions = (summary?.action_required_count || 0) > 0;

  // Compact view for dashboard widget
  if (compact) {
    return (
      <Card 
        className={`border ${hasUrgent || hasCritical ? 'border-red-300 bg-red-50/50' : 'border-slate-200'}`}
        data-testid="risk-flags-panel-compact"
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${hasUrgent || hasCritical ? 'text-red-500' : 'text-amber-500'}`} />
              Risk Flags
            </CardTitle>
            {totalFlags > 0 && (
              <Badge className={hasUrgent || hasCritical ? 'bg-red-600' : 'bg-amber-500'}>
                {totalFlags}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {totalFlags === 0 ? (
            <div className="flex items-center gap-2 text-green-600 py-2">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">No active risk flags</span>
            </div>
          ) : (
            <div className="space-y-2">
              {hasUrgent && (
                <div className="flex items-center gap-2 text-red-600">
                  <Zap className="w-4 h-4" />
                  <span className="text-sm font-bold">{summary.urgent_queue_count} LIVE ISSUES</span>
                </div>
              )}
              {hasCritical && (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">{summary.critical_count} critical items</span>
                </div>
              )}
              {hasUpcoming && (
                <div className="flex items-center gap-2 text-amber-600">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">{summary.upcoming_count} items expiring soon</span>
                </div>
              )}
              {hasActions && (
                <div className="flex items-center gap-2 text-blue-600">
                  <FileWarning className="w-4 h-4" />
                  <span className="text-sm">{summary.action_required_count} actions required</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full panel view
  return (
    <Card className="border-slate-200" data-testid="risk-flags-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${hasUrgent || hasCritical ? 'text-red-500' : 'text-amber-500'}`} />
            Risk & Action Flags
          </CardTitle>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-xs text-slate-500">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchRiskFlags}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary badges */}
        <div className="flex flex-wrap gap-2">
          {hasUrgent && (
            <Badge className="bg-red-600 animate-pulse">
              {summary?.urgent_queue_count || 0} LIVE ISSUES
            </Badge>
          )}
          <Badge className={`${hasCritical ? 'bg-red-600' : 'bg-slate-400'}`}>
            {summary?.critical_count || 0} Critical
          </Badge>
          <Badge className={`${hasUpcoming ? 'bg-amber-500' : 'bg-slate-400'}`}>
            {summary?.upcoming_count || 0} Upcoming
          </Badge>
          <Badge className={`${hasActions ? 'bg-blue-500' : 'bg-slate-400'}`}>
            {summary?.action_required_count || 0} Actions
          </Badge>
        </div>

        {totalFlags === 0 ? (
          <div className="flex items-center justify-center gap-2 text-green-600 py-6 bg-green-50 rounded-lg">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">No active risk flags</span>
          </div>
        ) : (
          <>
            {/* URGENT QUEUE - Live Booking Issues (highest priority) */}
            {urgent_queue && urgent_queue.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-bold text-red-700 flex items-center gap-1 bg-red-100 px-2 py-1 rounded">
                  <Zap className="w-4 h-4" />
                  URGENT: Live Booking Issues ({urgent_queue.length})
                </h4>
                <div className="space-y-2">
                  {urgent_queue.slice(0, 10).map((item, idx) => (
                    <div 
                      key={`urgent-${idx}`}
                      className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-150 ${
                        item.escalation_tier >= 3 ? 'bg-red-100 border-red-400 hover:bg-red-50' : 
                        item.escalation_tier >= 2 ? 'bg-orange-100 border-orange-400 hover:bg-orange-50' :
                        'bg-amber-50 border-amber-300 hover:bg-amber-100'
                      }`}
                      onClick={() => handleActionClick(item.action_url)}
                      data-testid={`urgent-item-${idx}`}
                    >
                      <div className={`mt-0.5 ${item.escalation_tier >= 3 ? 'text-red-600' : 'text-orange-600'}`}>
                        {getTypeIcon(item.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {getEscalationBadge(item.escalation_tier)}
                          {item.booking_ref && (
                            <span className="text-xs font-mono bg-slate-200 px-1.5 py-0.5 rounded">#{item.booking_ref}</span>
                          )}
                        </div>
                        <p className={`text-sm font-semibold mt-1 ${item.escalation_tier >= 3 ? 'text-red-800' : 'text-orange-800'}`}>
                          {item.message}
                        </p>
                        {/* Vehicle & Host Detail */}
                        {item.detail && (
                          <p className="text-xs text-slate-700 mt-0.5 flex items-center gap-1">
                            <Car className="w-3 h-3" />
                            {item.detail}
                          </p>
                        )}
                        {/* Due/Overdue Info */}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-xs font-medium ${item.escalation_tier >= 2 ? 'text-red-700' : 'text-amber-700'}`}>
                            {item.urgency_label}
                          </span>
                          {item.due_info && (
                            <span className="text-xs text-slate-500">
                              • {item.due_info}
                            </span>
                          )}
                        </div>
                        {/* Protection Note */}
                        {item.protection_note && (
                          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                            <Shield className="w-3 h-3" />
                            {item.protection_note}
                          </p>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        className={`flex-shrink-0 ${
                          item.escalation_tier >= 3 ? 'bg-red-600 hover:bg-red-700' : 
                          item.escalation_tier >= 2 ? 'bg-orange-600 hover:bg-orange-700' :
                          'bg-amber-600 hover:bg-amber-700'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActionClick(item.action_url);
                        }}
                      >
                        {item.action_label}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  ))}
                  {urgent_queue.length > 10 && (
                    <p className="text-xs text-red-600 text-center font-medium">
                      +{urgent_queue.length - 10} more urgent items
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Critical Items - Compliance Issues */}
            {critical && critical.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-red-700 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Critical Compliance ({critical.length})
                </h4>
                <div className="space-y-2">
                  {critical.slice(0, 5).map((item, idx) => (
                    <div 
                      key={`critical-${idx}`}
                      className="flex items-start gap-3 p-3 bg-red-50 border-2 border-red-300 rounded-lg cursor-pointer hover:bg-red-100 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-150"
                      onClick={() => handleActionClick(item.action_url)}
                      data-testid={`critical-item-${idx}`}
                    >
                      <div className="text-red-600 mt-0.5">
                        {getTypeIcon(item.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-red-800">{item.message}</p>
                        {item.due_date && (
                          <p className="text-xs text-red-600 mt-0.5">
                            Expired: {item.due_date}
                          </p>
                        )}
                        {item.action_label && (
                          <p className="text-xs text-red-600 mt-1 flex items-center gap-1 font-medium">
                            <ExternalLink className="w-3 h-3" />
                            {item.action_label}
                          </p>
                        )}
                      </div>
                      <Badge className="bg-red-600 text-white text-xs">
                        EXPIRED
                      </Badge>
                    </div>
                  ))}
                  {critical.length > 5 && (
                    <p className="text-xs text-red-600 text-center">
                      +{critical.length - 5} more critical items
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Upcoming Items */}
            {upcoming && upcoming.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-amber-700 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Expiring Soon ({upcoming.length})
                </h4>
                <div className="space-y-2">
                  {upcoming.slice(0, 5).map((item, idx) => (
                    <div 
                      key={`upcoming-${idx}`}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-150 ${
                        item.severity === 'warning' 
                          ? 'bg-amber-50 border-amber-300 hover:bg-amber-100' 
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      }`}
                      onClick={() => handleActionClick(item.action_url)}
                      data-testid={`upcoming-item-${idx}`}
                    >
                      <div className={item.severity === 'warning' ? 'text-amber-600 mt-0.5' : 'text-slate-500 mt-0.5'}>
                        {getTypeIcon(item.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${item.severity === 'warning' ? 'text-amber-800' : 'text-slate-700'}`}>
                          {item.message}
                        </p>
                        {item.due_date && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            Expires: {item.due_date}
                          </p>
                        )}
                        {item.action_label && (
                          <p className={`text-xs mt-1 flex items-center gap-1 ${item.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'}`}>
                            <ExternalLink className="w-3 h-3" />
                            {item.action_label}
                          </p>
                        )}
                      </div>
                      <Badge className={`text-xs ${
                        item.severity === 'warning' 
                          ? 'bg-amber-100 text-amber-800 border border-amber-300' 
                          : 'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}>
                        {item.severity === 'warning' ? 'Soon' : 'Info'}
                      </Badge>
                    </div>
                  ))}
                  {upcoming.length > 5 && (
                    <p className="text-xs text-amber-600 text-center">
                      +{upcoming.length - 5} more upcoming items
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Action Required Items */}
            {action_required && action_required.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-blue-700 flex items-center gap-1">
                  <FileWarning className="w-4 h-4" />
                  Action Required ({action_required.length})
                </h4>
                <div className="space-y-2">
                  {action_required.slice(0, 5).map((item, idx) => (
                    <div 
                      key={`action-${idx}`}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-150 ${
                        item.severity === 'warning' 
                          ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                          : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                      }`}
                      onClick={() => handleActionClick(item.action_url)}
                      data-testid={`action-item-${idx}`}
                    >
                      <div className={item.severity === 'warning' ? 'text-amber-600 mt-0.5' : 'text-blue-600 mt-0.5'}>
                        {getTypeIcon(item.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${item.severity === 'warning' ? 'text-amber-800' : 'text-blue-800'}`}>
                          {item.message}
                        </p>
                        {item.urgency_label && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            {item.urgency_label}
                          </p>
                        )}
                        {item.action_label && (
                          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            {item.action_label}
                          </p>
                        )}
                      </div>
                      <Badge className={`text-xs ${
                        item.severity === 'warning'
                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                          : 'bg-blue-100 text-blue-800 border border-blue-200'
                      }`}>
                        {item.type.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Data Notes */}
        {data_notes && (
          <div className="pt-2 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              {data_notes.users_with_licence_data > 0 
                ? `Licence data: ${data_notes.users_with_licence_data} users tracked`
                : 'No licence expiry data stored yet'
              }
              {data_notes.insurance_expiry_implemented && data_notes.vehicles_missing_insurance > 0 && 
                ` • ${data_notes.vehicles_missing_insurance} vehicles missing insurance data`
              }
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RiskFlagsPanel;
